/**
 * Microsoft Graph API Client using MSAL
 */

import { Client } from "@microsoft/microsoft-graph-client";
import { IPublicClientApplication } from "@azure/msal-browser";
import { graphScopes, msalInstance } from "@/config/authConfig";

export class GraphClient {
  private static instance: GraphClient;
  private client: Client | null = null;
  private accessToken: string | null = null;
  private tokenExpiresOn: Date | null = null;
  private refreshCallback: (() => Promise<{ accessToken: string; expiresOn: Date | null }>) | null =
    null;
  pca: IPublicClientApplication | undefined = undefined;

  private constructor() {}

  public static getInstance(): GraphClient {
    if (!GraphClient.instance) {
      GraphClient.instance = new GraphClient();
    }
    return GraphClient.instance;
  }

  public setRefreshCallback(
    callback: () => Promise<{ accessToken: string; expiresOn: Date | null }>
  ) {
    this.refreshCallback = callback;
  }

  /**
   * Check if the token is expired or about to expire (within 5 minutes)
   */
  private isTokenExpired(): boolean {
    if (!this.tokenExpiresOn) {
      return true;
    }
    const now = new Date();
    const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
    return this.tokenExpiresOn <= fiveMinutesFromNow;
  }

  /**
   * Refresh the access token
   */
  private async refreshToken(): Promise<string> {
    try {
      //refresh token in teams app
      if (this.refreshCallback) {
        const result = await this.refreshCallback();
        this.setAccessToken(result.accessToken, result.expiresOn);
        return result.accessToken;
      }

      const activeAccount = msalInstance?.getActiveAccount();
      if (!activeAccount) {
        throw new Error("No active account found");
      }

      const result = await msalInstance?.acquireTokenSilent({
        ...graphScopes,
        account: activeAccount,
        forceRefresh: true
      });

      if (result?.accessToken) {
        this.setAccessToken(result.accessToken, result.expiresOn || null);
        return result.accessToken;
      }

      throw new Error("Failed to refresh token");
    } catch (error) {
      console.error("Token refresh failed:", error);
      throw error;
    }
  }

  /**
   * Get a valid access token, refreshing if necessary
   */
  public async getValidToken(): Promise<string> {
    if (this.isTokenExpired()) {
      console.log("Token expired or about to expire, refreshing...");
      return await this.refreshToken();
    }
    return this.accessToken!;
  }

  public async initializeWithToken(token: string, expiresOn?: Date | string | null): Promise<void> {
    this.setAccessToken(token, expiresOn);
  }
  /**
   * Update the access token
   */
  public setAccessToken(token: string, expiresOn?: Date | string | null): void {
    this.accessToken = token;
    this.tokenExpiresOn = expiresOn ? new Date(expiresOn) : null;
    this.client = Client.init({
      authProvider: async (done) => {
        try {
          const validToken = await this.getValidToken();
          done(null, validToken);
        } catch (error) {
          done(error as Error, null);
        }
      }
    });
  }

  /**
   * Get the Graph client instance
   */
  public getClient(): Client {
    if (!this.client) {
      throw new Error("Graph client not initialized. Call initialize() first.");
    }
    return this.client;
  }

  /**
   * Check if the client is initialized
   */
  public isInitialized(): boolean {
    return this.client !== null && this.accessToken !== null;
  }

  /**
   * Clear the client instance
   */
  public clear(): void {
    this.client = null;
    this.accessToken = null;
    this.tokenExpiresOn = null;
  }

  
  public async executeWithRetry<T>(apiCall: (client: Client) => Promise<T>): Promise<T> {
    const client = this.getClient();
    try {
      return await apiCall(client);
    } catch (error: any) {
      const statusCode = error?.statusCode || error?.response?.status || error?.code;
      if (statusCode === 401 || error?.message?.includes("401")) {
        console.log("Received 401, attempting to refresh token and retry...");
        try {
          await this.refreshToken();
          const newClient = this.getClient();
          return await apiCall(newClient);
        } catch (refreshError) {
          console.error("Token refresh failed during retry:", refreshError);
          throw error;
        }
      }
      throw error;
    }
  }

  /**
   * Get current user profile
   */
  public async getMe(): Promise<any> {
    return await this.executeWithRetry((client) => client.api("/me").get());
  }

  /**
   * Get user's calendar
   */
  public async getCalendar(): Promise<any> {
    return await this.executeWithRetry((client) => client.api("/me/calendar").get());
  }

  /**
   * Get user's events
   */
  public async getEvents(startDateTime?: string, endDateTime?: string): Promise<any> {
    return await this.executeWithRetry(async (client) => {
      let query = client.api("/me/events");

      if (startDateTime && endDateTime) {
        query = query.filter(
          `start/dateTime ge '${startDateTime}' and end/dateTime le '${endDateTime}'`
        );
      }

      return await query
        .select("subject,start,end,location,attendees,organizer")
        .orderby("start/dateTime")
        .get();
    });
  }

  /**
   * Get calendar view (events in a specific time range)
   */
  public async getCalendarView(startDateTime: string, endDateTime: string): Promise<any> {
    return await this.executeWithRetry((client) =>
      client
        .api("/me/calendarView")
        .query({
          startDateTime: startDateTime,
          endDateTime: endDateTime
        })
        .select("subject,start,end,location,attendees,organizer,isAllDay")
        .orderby("start/dateTime")
        .get()
    );
  }

  /**
   * Create a new event
   */
  public async createEvent(event: any): Promise<any> {
    return await this.executeWithRetry((client) => client.api("/me/events").post(event));
  }

  /**
   * Create a new event for a specific user
   */
  public async createEventForUser(adId: string, event: any): Promise<any> {
    return await this.executeWithRetry((client) => client.api(`/users/${adId}/events`).post(event));
  }

  /**
   * Update an event
   */
  public async updateEvent(adId: string, eventId: string, event: any): Promise<any> {
    return await this.executeWithRetry((client) =>
      client.api(`/users/${adId}/events/${eventId}`).patch(event)
    );
  }

  /**
   * Get user's outlook categories
   */
  public async getUserCategories(adId: string): Promise<any> {
    return await this.executeWithRetry((client) =>
      client.api(`/users/${adId}/outlook/masterCategories`).get()
    );
  }

  /**
   * Delete an event
   */
  public async deleteEvent(eventId: string, userId: string): Promise<void> {
    await this.executeWithRetry((client) =>
      client.api(`/users/${userId}/events/${eventId}`).delete()
    );
  }

  /**
   * Cancel an event (organizer only)
   */
  public async cancelEvent(eventId: string, userId: string, comment?: string): Promise<void> {
    const body = comment ? { Comment: comment } : {};
    await this.executeWithRetry((client) =>
      client.api(`/users/${userId}/events/${eventId}/cancel`).post(body)
    );
  }

  /**
   * Get user's photo
   */
  public async getUserPhoto(): Promise<Blob> {
    return await this.executeWithRetry((client) => client.api("/me/photo/$value").get());
  }

  /**
   * Get specific user's photo by ID or email
   */
  public async getUserPhotoById(userIdOrEmail: string): Promise<Blob | null> {
    try {
      return await this.executeWithRetry((client) =>
        client.api(`/users/${userIdOrEmail}/photo/$value`).get()
      );
    } catch (error) {
      // User may not have a photo
      return null;
    }
  }

  /**
   * Get multiple users' photos in batch
   * @param userIdsOrEmails Array of user IDs or emails
   * @returns Map of email/ID to photo blob URL
   */
  public async getBatchUserPhotos(userIdsOrEmails: string[]): Promise<Map<string, string>> {
    const client = this.getClient();
    const photoMap = new Map<string, string>();

    const batchSize = 20;
    const batches: string[][] = [];

    for (let i = 0; i < userIdsOrEmails.length; i += batchSize) {
      batches.push(userIdsOrEmails.slice(i, i + batchSize));
    }

    for (const batch of batches) {
      const batchRequest = {
        requests: batch.map((userId, index) => ({
          id: index.toString(),
          method: "GET",
          url: `/users/${userId}/photo/$value`
        }))
      };

      try {
        const response = await client.api("/$batch").post(batchRequest);

        if (response.responses) {
          for (let i = 0; i < response.responses.length; i++) {
            const res = response.responses[i];
            const userId = batch[i];

            if (res.status === 200 && res.body) {
              try {
                const base64Data = res.body;
                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let j = 0; j < byteCharacters.length; j++) {
                  byteNumbers[j] = byteCharacters.charCodeAt(j);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: "image/jpeg" });
                const url = URL.createObjectURL(blob);
                photoMap.set(userId, url);
              } catch (error) {
                console.error(`Error processing photo for ${userId}:`, error);
              }
            }
          }
        }
      } catch (error) {
        console.error("Batch photo request failed:", error);
      }
    }

    return photoMap;
  }

  /**
   * Get user's calendar with timezone information
   */
  public async getCalendarWithTimezone(): Promise<any> {
    const client = this.getClient();
    return await client.api("/me/calendar").select("id,name").get();
  }

  /**
   * Get calendar permissions (with pagination support)
   */
  public async getCalendarPermissions(): Promise<any> {
    const client = this.getClient();
    let allPermissions: any[] = [];
    let nextLink: string | undefined = "/me/calendar/calendarPermissions";

    // Fetch all pages
    while (nextLink) {
      const response = await client.api(nextLink).get();

      if (response.value && Array.isArray(response.value)) {
        allPermissions = allPermissions.concat(response.value);
      }

      // Check for next page
      nextLink = response["@odata.nextLink"];
    }

    return { value: allPermissions };
  }

  /**
   * Get allowed roles for a calendar permission
   * This checks what permission roles are allowed by attempting to get calendar
   * permission metadata or checking organization membership
   */
  public async getAllowedRolesForEmail(email: string): Promise<string[]> {
    const client = this.getClient();
    try {
      const existingPerms = await client.api("/me/calendar/calendarPermissions").get();
      const existingPerm = existingPerms.value?.find(
        (p: any) => p.emailAddress?.address?.toLowerCase() === email.toLowerCase()
      );

      if (existingPerm && existingPerm.allowedRoles) {
        return existingPerm.allowedRoles;
      }

      try {
        const userSearch = await client
          .api("/users")
          .filter(`mail eq '${email}' or userPrincipalName eq '${email}'`)
          .select("id,mail,userPrincipalName")
          .get();
        const isInternalUser = userSearch.value && userSearch.value.length > 0;

        if (isInternalUser) {
          return [
            "freeBusyRead",
            "limitedRead",
            "read",
            "write",
            "delegateWithoutPrivateEventAccess",
            "delegateWithPrivateEventAccess"
          ];
        }
      } catch (error) {
        console.log("User not found in directory, treating as external");
      }

      return ["freeBusyRead", "limitedRead", "read"];
    } catch (error) {
      console.warn("Could not determine allowed roles:", error);
      return ["freeBusyRead", "limitedRead", "read"];
    }
  }

  /**
   * Create calendar permission (share calendar with user)
   */
  public async createCalendarPermission(permission: {
    emailAddress: {
      name?: string;
      address: string;
    };
    role: string;
  }): Promise<any> {
    const client = this.getClient();
    return await client.api("/me/calendar/calendarPermissions").post(permission);
  }

  /**
   * Update calendar permission
   */
  public async updateCalendarPermission(permissionId: string, role: string): Promise<any> {
    const client = this.getClient();
    return await client.api(`/me/calendar/calendarPermissions/${permissionId}`).patch({ role });
  }

  /**
   * Delete calendar permission
   */
  public async deleteCalendarPermission(permissionId: string): Promise<void> {
    const client = this.getClient();
    await client.api(`/me/calendar/calendarPermissions/${permissionId}`).delete();
  }

  /**
   * Find meeting times based on attendees' availability
   */
  public async findMeetingTimes(params: {
    attendees: Array<{
      type: "required" | "optional";
      emailAddress: {
        name?: string;
        address: string;
      };
    }>;
    timeConstraint?: {
      activityDomain?: "work" | "personal" | "unrestricted";
      timeSlots?: Array<{
        start: {
          dateTime: string;
          timeZone: string;
        };
        end: {
          dateTime: string;
          timeZone: string;
        };
      }>;
    };
    meetingDuration?: string;
    maxCandidates?: number;
    isOrganizerOptional?: boolean;
    returnSuggestionReasons?: boolean;
    minimumAttendeePercentage?: number;
    locationConstraint?: {
      isRequired?: boolean;
      suggestLocation?: boolean;
      locations?: Array<{
        displayName: string;
        resolveAvailability?: boolean;
      }>;
    };
  }): Promise<any> {
    const client = this.getClient();
    const apiPath = "/me/findMeetingTimes";

    return await client.api(apiPath).post(params);
  }

  /**
   * Get list of people relevant to the user
   * @param options Optional query parameters
   * @returns List of person objects
   */
  public async getPeople(options?: {
    search?: string;
    filter?: string;
    orderBy?: string;
    select?: string[];
    top?: number;
    skip?: number;
  }): Promise<any> {
    const client = this.getClient();
    let query = client.api("/me/people");

    if (options?.search) {
      query = query.search(`"${options.search}"`);
    }

    if (options?.filter) {
      query = query.filter(options.filter);
    }

    if (options?.orderBy) {
      query = query.orderby(options.orderBy);
    }

    if (options?.select && options.select.length > 0) {
      query = query.select(options.select.join(","));
    }

    if (options?.top) {
      query = query.top(options.top);
    }

    if (options?.skip) {
      query = query.skip(options.skip);
    }

    return await query.get();
  }
}

export const graphClient = GraphClient.getInstance();
